import { Dialog, DialogContent, Icon, IconButton } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import React, { useMemo, useState } from "react";
import { Cell } from "react-table";
import { Task } from "generated/graphql";

function getNotifiablePath(type?: string | null, id?: string | number | null) {
  if (id === null || id === undefined || id === "") return null;

  const normalizedType = (type ?? "").toLowerCase();
  const pathByType: Record<string, (notifiableId: string | number) => string> = {
    proposal: (notifiableId) => `/proposals/${notifiableId}/info`,
    job: (notifiableId) => `/jobs/${notifiableId}`,
    job_phase: (notifiableId) => `/jobs/${notifiableId}`,
    opportunity: (notifiableId) => `/opportunities/${notifiableId}/update`,
  };

  const matchedKey = Object.keys(pathByType).find((key) => normalizedType.includes(key));
  if (matchedKey) {
    const rawPath = pathByType[matchedKey](id);
    const separator = rawPath.includes("?") ? "&" : "?";
    return `${rawPath}${separator}embedded=1`;
  }

  return null;
}

export default function TaskNotifiableIdCell({ row, value }: Cell<Task>) {
  const [open, setOpen] = useState(false);

  const notifiableType = row.original.notifiableType;

  // Internal ID (used for routing / iframe src)
  const internalId = (value ?? row.original.notifiableId) ?? null;

  // External ID (used for display)
  const externalId = row.original.notifiableExternalId ?? null;

  const displayId = externalId ?? internalId;

  const path = useMemo(
    () => getNotifiablePath(notifiableType, internalId),
    [notifiableType, internalId]
  );

  if (!internalId && !externalId) {
    return (
      <MDTypography variant="button" sx={{ opacity: 0.6 }}>
        —
      </MDTypography>
    );
  }

  if (!path) {
    return <MDTypography variant="button">{String(displayId)}</MDTypography>;
  }

  return (
    <>
      <MDTypography
        variant="button"
        fontWeight="medium"
        onClick={(event: React.MouseEvent<HTMLSpanElement>) => {
          event.preventDefault();
          event.stopPropagation();
          setOpen(true);
        }}
        sx={{
          cursor: "pointer",
          textDecoration: "underline",
          display: "inline-block",
        }}
        title={`Open ${notifiableType ?? "record"} ${String(displayId)}`}
      >
        {String(displayId)}
      </MDTypography>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xl" fullWidth>
        <DialogContent sx={{ p: 0, position: "relative" }}>
          <IconButton
            onClick={() => setOpen(false)}
            size="small"
            title="Close"
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 2,
              backgroundColor: "rgba(255,255,255,0.9)",
              "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
          <iframe
            src={path}
            title={`${notifiableType ?? "Record"} ${String(displayId)}`}
            style={{ width: "100%", height: "85vh", border: 0 }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
